import { useEffect, useState } from "react";
import { callFunction } from "../lib/supabase.js";
import { CouplePortal, fmt } from "../dashboard.jsx";
import { Loading, Message, ErrorText } from "./common.jsx";
import { StatusTag } from "../ui.jsx";

export function PublicPortal({ token }) {
  const [data, setData] = useState(undefined);
  const [err, setErr] = useState("");
  const [section, setSection] = useState(null);

  const load = () => callFunction("portal", { action: "get", token })
    .then(d => { setData(d); setSection(s => s || ((d.quote && d.quote.canRespond) || (d.contract && d.contract.canRespond) ? "approvals" : "profile")); })
    .catch(e => { setErr(e.message); setData(null); });
  useEffect(() => { load(); }, [token]);

  if (data === undefined) return <Loading />;
  if (!data) return <Message title="This portal link isn't working" body={err || "It may have been turned off. Ask your planner for a new link."} />;

  const hasDocs = !!(data.quote || data.contract);
  const waiting = (data.quote && data.quote.canRespond) || (data.contract && data.contract.canRespond);
  const extraSections = hasDocs ? [{ key: "approvals", label: "Proposal & agreement", first: true, badge: waiting, render: () => <Approvals data={data} token={token} onDone={load} /> }] : [];

  return <CouplePortal client={data.client} vendors={data.vendors} portalSection={section || "profile"} setPortalSection={setSection} extraSections={extraSections} studioName={data.studioName} />;
}

// "The proposal" and "The agreement", set like the brand deck's pricing card:
// Courier label, Cormorant title, Courier price, one oxblood action.
function Card({ label, title, tag, children }) {
  return (
    <section className="panel measure deck-card" style={{ "--measure": "780px" }} aria-label={title}>
      <div className="panel-head" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div className="label">{label}</div>
          <h2 className="panel-title" style={{ fontSize: 34, marginTop: 6 }}>{title}</h2>
        </div>
        {tag && <StatusTag status={tag} />}
      </div>
      {children}
    </section>
  );
}

function Approvals({ data, token, onDone }) {
  // Oxblood once per view: only the first thing waiting on the couple gets the oxblood button.
  const quoteFirst = !!(data.quote && data.quote.canRespond);
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      {data.quote && <QuoteCard quote={data.quote} token={token} onDone={onDone} studioName={data.studioName} primary={quoteFirst} />}
      {data.contract && <ContractCard contract={data.contract} token={token} onDone={onDone} primary={!quoteFirst} />}
    </div>
  );
}

function useRespond(token, kind, onDone) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const respond = async (action, extra, doneMsg) => {
    setBusy(true); setErr("");
    try { await callFunction("portal", { action: "respond", token, kind, response: action, ...extra }); setDone(doneMsg); await onDone(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return { busy, err, done, respond };
}

function ChangesBox({ busy, onSend, onCancel }) {
  const [text, setText] = useState("");
  return (
    <div className="stack" style={{ "--gap": "12px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
      <label className="stack" style={{ "--gap": "8px" }}>
        <span className="field-label">What would you like changed?</span>
        <textarea className="input" style={{ minHeight: 110 }} value={text} onChange={e => setText(e.target.value)}></textarea>
      </label>
      <div className="row" style={{ "--gap": "10px" }}>
        <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => onSend(text)}>{busy ? "Sending…" : "Send my note"}</button>
        <button className="btn btn-secondary" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function QuoteCard({ quote, token, onDone, studioName, primary }) {
  const { busy, err, done, respond } = useRespond(token, "quote", onDone);
  const [asking, setAsking] = useState(false);
  const investment = quote.finalNumber ? fmt(quote.finalNumber) : fmt(quote.rangeLow) + " – " + fmt(quote.rangeHigh);
  const tag = quote.status === "Client approved" ? "Approved" : quote.status === "Client declined" ? "Declined" : quote.canRespond ? "Awaiting your review" : null;
  return (
    <Card label="Your planning proposal" title={quote.packageTier || "The proposal"} tag={tag}>
      <div>
        <div className="label">Planning investment</div>
        <div className="deck-price">{investment}</div>
      </div>
      {quote.lineItems.length > 0 && (
        <div>
          {quote.lineItems.map((li, i) => (
            <div key={i} className="list-row list-split" style={{ fontSize: 16 }}>
              <span>{li.label}{li.note ? <span className="text-muted" style={{ display: "block", fontSize: 15 }}>{li.note}</span> : null}</span>
              <span className="figure">{fmt(li.amount)}</span>
            </div>
          ))}
        </div>
      )}
      {done && <div role="status" className="note note-ok">{done}</div>}
      {quote.canRespond && !asking && (
        <div className="row" style={{ "--gap": "10px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
          <button className={"btn " + (primary ? "btn-accent" : "btn-primary")} disabled={busy} onClick={() => { if (window.confirm("Approve this proposal? I'll prepare your agreement next.")) respond("approve", {}, "Thank you. I've been told, and I'll send your agreement next."); }}>{busy ? "Saving…" : "Approve proposal"}</button>
          <button className="btn btn-secondary" disabled={busy} onClick={() => setAsking(true)}>Request changes</button>
        </div>
      )}
      {quote.canRespond && asking && <ChangesBox busy={busy} onCancel={() => setAsking(false)} onSend={t => respond("changes", { comment: t }, "Sent. I'll update the proposal and get back to you.").then(() => setAsking(false))} />}
      <ErrorText>{err}</ErrorText>
    </Card>
  );
}

function ContractCard({ contract, token, onDone, primary }) {
  const { busy, err, done, respond } = useRespond(token, "contract", onDone);
  const [asking, setAsking] = useState(false);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const signed = contract.status === "Signed";
  return (
    <Card label="Your planning agreement" title="The agreement" tag={signed ? "Signed" : contract.canRespond ? "Ready to sign" : null}>
      {/* The agreement reads like a document on white paper. */}
      <div className="agreement-paper" tabIndex={0} role="region" aria-label="Agreement text"><AgreementText text={contract.body} /></div>
      {signed && contract.signature && (
        <div className="signature-block">
          <img src="/brand/wax-seal.svg" alt="" width="44" height="44" />
          <div>
            <div className="label" style={{ color: "var(--ink)" }}>Signed by {contract.signature.name}</div>
            <div className="label">{new Date(contract.signature.at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</div>
          </div>
        </div>
      )}
      {done && !signed && <div role="status" className="note note-ok">{done}</div>}
      {contract.canRespond && !asking && (
        <div className="stack" style={{ "--gap": "14px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
          <label className="stack" style={{ "--gap": "8px", maxWidth: 420 }}>
            <span className="field-label">Type your full legal name to sign</span>
            <input className="input signature-input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
          </label>
          <label style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 16, lineHeight: 1.5, cursor: "pointer" }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
            <span>I have read this agreement and agree that typing my name above is my electronic signature.</span>
          </label>
          <div className="row" style={{ "--gap": "10px" }}>
            <button className={"btn " + (primary ? "btn-accent" : "btn-primary")} disabled={busy || !agree || name.trim().length < 3} onClick={() => respond("approve", { signedName: name.trim() }, "Signed. Thank you; I'll take it from here.")}>{busy ? "Signing…" : "Sign agreement"}</button>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setAsking(true)}>Request changes</button>
          </div>
        </div>
      )}
      {contract.canRespond && asking && <ChangesBox busy={busy} onCancel={() => setAsking(false)} onSend={t => respond("changes", { comment: t }, "Sent. I'll revise the agreement and send it to you again.").then(() => setAsking(false))} />}
      <ErrorText>{err}</ErrorText>
    </Card>
  );
}

// Light Markdown (headings, bold lines, bullets) → readable text; never injects HTML.
function AgreementText({ text }) {
  return String(text || "").split("\n").map((raw, i) => {
    const t = raw.trim();
    if (!t) return <div key={i} style={{ height: 10 }} />;
    const level = (t.match(/^(#{1,6})\s+/) || [])[1];
    const heading = !!level || /^\*\*[^*]+\*\*$/.test(t);
    const clean = t.replace(/^#{1,6}\s+/, "").replace(/\*\*/g, "").replace(/^_(.*)_$/, "$1");
    if (heading) return level === "#" ? <h3 key={i} className="agreement-title">{clean}</h3> : <h4 key={i} className="agreement-heading">{clean}</h4>;
    if (/^[-*]\s+/.test(t)) return <div key={i} style={{ paddingLeft: 18, textIndent: -12 }}>• {clean.replace(/^[-*]\s+/, "")}</div>;
    return <p key={i} style={{ margin: 0 }}>{clean}</p>;
  });
}
